import type { ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/* The status and MCE-type chip colours from constants.js (Colors.quoteStatus, Colors.mceStatus,
   TextColors), as the same-valued tokens in index.css (D5). A key with no colour of its own gets
   today's fallback: gray.100 behind gray.600 text. */

const STATUS: Record<string, string> = {
  Active: 'bg-status-active-bg text-status-active-fg',
  Executed: 'bg-status-active-bg text-status-active-fg',
  Inactive: 'bg-status-inactive-bg text-status-inactive-fg',
  Draft: 'bg-status-draft-bg text-status-draft-fg',
  Pending: 'bg-status-pending-bg text-status-pending-fg',
  Superseded: 'bg-status-superseded-bg text-status-unknown-fg',
  Cancelled: 'bg-status-cancelled-bg text-status-cancelled-fg',
  Reversal: 'bg-status-reversal-bg text-status-reversal-fg',
  IVE: 'bg-ive-chip-bg text-ive-chip',
};

const MCE_TYPE: Record<string, string> = {
  retrospective: 'bg-status-active-bg text-status-active-fg',
  locationChange: 'bg-mce-location-change-bg text-mce-location-change-fg',
  legacy: 'bg-mce-legacy-bg text-status-inactive-fg',
  quarterly: 'bg-ive-chip-bg text-ive-chip',
};

const FALLBACK = 'bg-status-unknown-bg text-status-unknown-fg';

export const statusBadgeClass = (status: string | undefined) => (status && STATUS[status]) || FALLBACK;
export const mceTypeBadgeClass = (type: string | undefined) => (type && MCE_TYPE[type]) || FALLBACK;

/** A quote's or application's stage, as the Quotes and Applications lists colour it (Colors.quoteStatus). */
export type QuoteTone = 'pending' | 'released' | 'prebound' | 'bound' | 'rejected' | 'expired' | 'none';

const QUOTE_TONE: Record<QuoteTone, string> = {
  pending: 'bg-quote-pending text-status-unknown-fg',
  released: 'bg-quote-released text-status-unknown-fg',
  prebound: 'bg-quote-prebound text-status-unknown-fg',
  bound: 'bg-quote-bound text-status-unknown-fg',
  rejected: 'bg-quote-rejected text-status-unknown-fg',
  expired: 'bg-quote-expired text-status-unknown-fg',
  none: FALLBACK,
};

export const quoteToneClass = (tone: QuoteTone) => QUOTE_TONE[tone];

/** A square-cornered status chip, as the legacy Chip drew it. */
export function StatusBadge({ className, children }: { className: string; children: ReactNode }) {
  return <Badge className={cn('h-auto rounded-md px-3.5 py-1.5 text-xs font-semibold uppercase', className)}>{children}</Badge>;
}
